import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import type { OperatorRow } from '@tennis-scanner/shared';

// Admin / Operators — directory of human operators in the tenant.
//
// V1 read-only: shows everyone's profile + bet-sizing overview. Promotion /
// demotion / deactivate require either SQL or a future admin endpoint
// (operators.updateRole). The header row mentions this.

function roleClass(role: OperatorRow['role']): string {
  if (role === 'admin') return 'border-ai-verdict/40 text-ai-verdict bg-ai-verdict/10';
  if (role === 'senior') return 'border-info/40 text-info bg-info/10';
  return 'border-muted-foreground/40 text-muted-foreground bg-muted';
}

export function AdminOperatorsPage() {
  const operators = trpc.operators.list.useQuery({ limit: 200 });

  return (
    <div className="space-y-4">
      <header>
        <h1
          className="font-brand text-3xl tracking-[0.25em] text-foreground"
          data-testid="page-title"
        >
          OPERADORES
        </h1>
        <p className="text-xs text-muted-foreground">
          Directorio de operadores. Por ahora cambiar rol o desactivar se hace desde SQL — UI
          dedicada llega en V2.
        </p>
      </header>

      {operators.isError && (
        <Alert variant="destructive">
          <AlertTitle>Error cargando operadores</AlertTitle>
          <AlertDescription>{operators.error.message}</AlertDescription>
        </Alert>
      )}

      {operators.isLoading && <Skeleton className="h-64" />}

      {operators.data && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead className="text-right">Kelly</TableHead>
                <TableHead className="text-right">Min edge</TableHead>
                <TableHead className="text-right">Edge mode</TableHead>
                <TableHead className="text-right">Actualizado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operators.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                    Sin operadores registrados.
                  </TableCell>
                </TableRow>
              )}
              {operators.data.map((op) => (
                <TableRow key={op.id}>
                  <TableCell className="font-medium text-foreground">{op.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('uppercase', roleClass(op.role))}>
                      {op.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        op.isActive
                          ? 'border-positive/40 text-positive bg-positive/10'
                          : 'border-destructive/40 text-destructive bg-destructive/10'
                      }
                    >
                      {op.isActive ? 'activo' : 'inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{op.timezone}</TableCell>
                  <TableCell className="text-right font-mono">
                    {op.kellyFraction.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {(op.minEdgePct * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{op.edgeMode}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                    {formatRelativeTime(op.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
